import { MultiSelectOption } from '@/models/MultiSelectOption';
import { TailwindWidthClass } from '@/models/TailwindWIdthClass';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Menu, Transition } from '@headlessui/react';
import { CSSProperties, Fragment, ReactNode, useEffect, type FC } from "react";

// OpenStateEffect bridges @headlessui Menu's render-prop `open` value into a useEffect.
// Hooks cannot be called inside render props, so this null-rendering component receives
// `open` as a prop and fires onOpenChange whenever it changes.
const OpenStateEffect: FC<{ open: boolean; onOpenChange: (open: boolean) => void }> = ({ open, onOpenChange }) => {
    useEffect(() => {
        onOpenChange(open);
    }, [open]);
    return null;
};

interface DropdownMenuProps {
    options: MultiSelectOption[],
    onSelected: (item: MultiSelectOption) => void,
    childClassName?: string,
    buttonClassName?: string,
    className?: string,
    cyName?: string,
    width?: TailwindWidthClass,
    children: ReactNode,
    direction?: 'top' | 'bottom',
    menuStyle?: CSSProperties,
    onOpenChange?: (open: boolean) => void
}

const DropdownSelector: FC<DropdownMenuProps> = ({ options, onSelected, childClassName, buttonClassName, className, cyName, width = 'w-56', children, direction = 'bottom', menuStyle, onOpenChange }) => {
    const getMenuPositionClasses = () => {
        if (direction === 'top') {
            return 'bottom-full mb-2 origin-bottom-right';
        }
        return 'mt-2 origin-top-right';
    };

    const getTransformOrigin = () => {
        if (direction === 'top') {
            return {
                enterFrom: 'transform opacity-0 scale-95 translate-y-1',
                enterTo: 'transform opacity-100 scale-100 translate-y-0',
                leaveFrom: 'transform opacity-100 scale-100 translate-y-0',
                leaveTo: 'transform opacity-0 scale-95 translate-y-1'
            };
        }
        return {
            enterFrom: 'transform opacity-0 scale-95 -translate-y-1',
            enterTo: 'transform opacity-100 scale-100 translate-y-0',
            leaveFrom: 'transform opacity-100 scale-100 translate-y-0',
            leaveTo: 'transform opacity-0 scale-95 -translate-y-1'
        };
    };

    const transformClasses = getTransformOrigin();

    return <div className={className}>
        <Menu as="div" className="relative inline-block" id="exportContainer">
            {({ open }) => (
                <>
                    {onOpenChange && <OpenStateEffect open={open} onOpenChange={onOpenChange} />}
                    <Menu.Button data-cy={cyName} className={buttonClassName ? buttonClassName : "flex flex-row items-center w-full justify-between pt-1 cursor-pointer"}>
                        <div className='cursor-pointer'>
                            {children}
                        </div>
                        <FontAwesomeIcon className={childClassName ? childClassName : `flex pl-1 text-xs`} icon={open ? faChevronUp : faChevronDown} />
                    </Menu.Button>
                    <Transition
                        show={open}
                        as={Fragment}
                        enter="transition ease-out duration-100"
                        enterFrom={transformClasses.enterFrom}
                        enterTo={transformClasses.enterTo}
                        leave="transition ease-in duration-75"
                        leaveFrom={transformClasses.leaveFrom}
                        leaveTo={transformClasses.leaveTo}>
                        <Menu.Items style={{ right: '-2rem', ...menuStyle }} className={`absolute ${getMenuPositionClasses()} z-50 ${width} rounded-md bg-white text-black shadow-xl ring-1 ring-black ring-opacity-5 focus:outline-none pb-2`}>
                            {(() => {
                                const hasHeaders = options?.some(o => o.isHeader);
                                const firstHeaderIndex = options?.findIndex(o => o.isHeader) ?? -1;
                                return options?.map((item, i) => {
                                    const nextItem = options[i + 1];
                                    const isLastInGroup = !nextItem || nextItem.isHeader;
                                    return item.isHeader ? (
                                        <div key={item.value} className={`flex items-center px-3 pb-1 ${i === firstHeaderIndex ? 'pt-2' : 'pt-3'} text-xs font-semibold text-gray-400 uppercase tracking-wide`}>
                                            {item.iconUrl && <img className="h-3.5 mr-1.5" src={item.iconUrl} alt={item.label} />}
                                            {item.label}
                                        </div>
                                    ) : (
                                        <Menu.Item key={item.value}>
                                            {({ active }) => (
                                                <button type='button' onClick={() => onSelected(item)}
                                                    className={`${active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'} flex w-full items-center ${hasHeaders ? 'pl-5 pr-3 py-1.5 hover:bg-blue-50' : 'px-4 py-2 hover:bg-gray-50'} text-sm transition-colors duration-150${hasHeaders && !isLastInGroup ? ' border-b border-gray-100' : ''}`}
                                                    data-cy={`MenuItem-${i}`}>
                                                    {item.iconUrl && <img className="h-5 mr-2" src={item.iconUrl} alt={item.label} />}
                                                    <span>{item?.label}</span>
                                                </button>
                                            )}
                                        </Menu.Item>
                                    );
                                });
                            })()}
                        </Menu.Items>
                    </Transition>
                </>
            )}
        </Menu>
    </div>
}

export default DropdownSelector;